export type InvoiceStatus = 'draft' | 'created' | 'annulled' | 'corrected' | 'paid' | 'partially_paid'

export interface InvoiceItem {
  id: number
  description: string
  quantity: number
  priceWithoutVat: number
  vatRate: number
  withholdingRate: number
  subtotalWithoutVat: number
  subtotalWithVat: number
}

export interface PaymentDetails {
  iban: string
  swift: string
  bankName: string
  accountHolder: string
  reference: string
}

export interface InvoiceSender {
  name: string
  nif: string
  // Structured address
  street?: string
  city?: string
  postalCode?: string
  countryCode?: string // ISO 3166-1 alpha-3
  // Legacy address field for backward compatibility
  address?: string
  email: string
  phone: string
  paymentDetails?: PaymentDetails
}

export interface InvoiceRecipient {
  name: string
  nif: string
  // Structured address
  street?: string
  city?: string
  postalCode?: string
  countryCode?: string // ISO 3166-1 alpha-3
  // Legacy address field for backward compatibility
  address?: string
}

export interface Invoice {
  id: number
  invoiceNumber: string
  date: string
  sender: InvoiceSender
  recipient: InvoiceRecipient
  items: InvoiceItem[]
  vatExemptionReason: string | null
  notes: string | null
  baseAmount: number
  totalVat: number
  totalWithholding: number
  totalAmount: number
  status: InvoiceStatus
  currency: string
  correctedInvoiceId: number | null
  isArchived: boolean
  // Optional links to saved entities
  clientId?: number
  senderProfileId?: number
  createdAt: string
  updatedAt: string
}

// Types for API requests
export interface InvoiceItemCreate {
  description: string
  quantity: number
  priceWithoutVat: number
  vatRate: number
  withholdingRate: number
}

export interface InvoiceCreate {
  invoiceNumber?: string
  date: string
  sender: InvoiceSender
  recipient: InvoiceRecipient
  items: InvoiceItemCreate[]
  vatExemptionReason?: string
  notes?: string
  currency?: string
  // Optional links to saved entities
  clientId?: number
  senderProfileId?: number
}

export interface InvoiceUpdate {
  date?: string
  sender?: InvoiceSender
  recipient?: InvoiceRecipient
  items?: InvoiceItemCreate[]
  vatExemptionReason?: string
  notes?: string
  currency?: string
}

export interface CorrectionInvoiceCreate {
  date: string
  sender: InvoiceSender
  recipient: InvoiceRecipient
  items: InvoiceItemCreate[]
  vatExemptionReason?: string
  notes?: string
  currency?: string
}

// Form item type (with string id for local state)
export interface InvoiceItemForm {
  id: string
  description: string
  quantity: number
  priceWithoutVat: number
  vatRate: number
  withholdingRate: number
  subtotalWithoutVat: number
  subtotalWithVat: number
}

export const VAT_RATES = [
  { value: 0, label: '0% (Exento)' },
  { value: 4, label: '4% (Superreducido)' },
  { value: 10, label: '10% (Reducido)' },
  { value: 21, label: '21% (General)' },
]

export const WITHHOLDING_RATES = [
  { value: 0, label: '0%' },
  { value: 7, label: '7%' },
  { value: 15, label: '15%' },
  { value: 19, label: '19%' },
]

export interface VatExemptionReason {
  value: string
  article: string
  labelKey: string
  descKey: string
}

export const VAT_EXEMPTION_REASONS: VatExemptionReason[] = [
  { value: '', article: '', labelKey: 'invoices.vatExemptions.selectReason', descKey: '' },
  // Exports
  { value: 'art21', article: 'Art. 21.1 LIVA', labelKey: 'invoices.vatExemptions.art21.label', descKey: 'invoices.vatExemptions.art21.desc' },
  { value: 'art21_2', article: 'Art. 21.2 LIVA', labelKey: 'invoices.vatExemptions.art21_2.label', descKey: 'invoices.vatExemptions.art21_2.desc' },
  { value: 'art22', article: 'Art. 22 LIVA', labelKey: 'invoices.vatExemptions.art22.label', descKey: 'invoices.vatExemptions.art22.desc' },
  // Intra-EU
  { value: 'art25', article: 'Art. 25 LIVA', labelKey: 'invoices.vatExemptions.art25.label', descKey: 'invoices.vatExemptions.art25.desc' },
  // Services outside Spain
  { value: 'art69', article: 'Art. 69.Uno.1 LIVA', labelKey: 'invoices.vatExemptions.art69.label', descKey: 'invoices.vatExemptions.art69.desc' },
  // Interior exemptions (specific)
  { value: 'art20_18', article: 'Art. 20.Uno.18 LIVA', labelKey: 'invoices.vatExemptions.art20_18.label', descKey: 'invoices.vatExemptions.art20_18.desc' },
  { value: 'art20_9', article: 'Art. 20.Uno.9 LIVA', labelKey: 'invoices.vatExemptions.art20_9.label', descKey: 'invoices.vatExemptions.art20_9.desc' },
  { value: 'art20_3', article: 'Art. 20.Uno.3 LIVA', labelKey: 'invoices.vatExemptions.art20_3.label', descKey: 'invoices.vatExemptions.art20_3.desc' },
  // Interior exemptions (general)
  { value: 'art20', article: 'Art. 20 LIVA', labelKey: 'invoices.vatExemptions.art20.label', descKey: 'invoices.vatExemptions.art20.desc' },
]
